import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Note {
  id: string;
  applicantId: string;
  content: string;
  author: string;
  timestamp: string; // ISO string for serialization
  isPinned: boolean;
}

export interface ActivityLogEntry {
  id: string;
  applicantId: string;
  timestamp: Date;
  author: string;
  action: string;
  details?: string;
}

export interface NotesState {
  notes: Note[];
  activityLog: ActivityLogEntry[];
  isLoading: boolean;
  error: string | null;
}

const initialState: NotesState = {
  notes: [],
  activityLog: [],
  isLoading: false,
  error: null,
};

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    setNotes: (state, action: PayloadAction<Note[]>) => {
      return { ...state, notes: action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoading: action.payload };
    },
    setError: (state, action: PayloadAction<string>) => {
      return { ...state, error: action.payload };
    },
    clearError: (state) => {
      return { ...state, error: null };
    },
    addNote: (state, action: PayloadAction<{ applicantId: string; content: string; author: string }>) => {
      const newNote: Note = {
        id: Date.now().toString(),
        applicantId: action.payload.applicantId,
        content: action.payload.content,
        author: action.payload.author,
        timestamp: new Date().toISOString(),
        isPinned: false,
      };
      state.notes.push(newNote);

       // Add to activity log
       const activityEntry: ActivityLogEntry = {
        id: `${Date.now().toString()  }_activity`,
        applicantId: action.payload.applicantId,
        timestamp: new Date(),
        author: action.payload.author,
        action: 'Added note',
        details: action.payload.content,
      };
      state.activityLog.push(activityEntry);
      return state;
    },
    
    toggleNotePin: (state, action: PayloadAction<{ noteId: string; author: string }>) => {
      const note = state.notes.find(n => n.id === action.payload.noteId);
      if (note) {
        note.isPinned = !note.isPinned;

        // Add to activity log
        const activityEntry: ActivityLogEntry = {
          id: `${Date.now().toString()  }_activity`,
          applicantId: note.applicantId,
          timestamp: new Date(),
          author: action.payload.author,
          action: note.isPinned ? 'Pinned note' : 'Unpinned note',
          details: note.content,
        };
        return { ...state, activityLog: [...state.activityLog, activityEntry] };
      }
      return state;
    },
    addActivityLogEntry: (state, action: PayloadAction<Omit<ActivityLogEntry, 'id' | 'timestamp'>>) => {
      const activityEntry: ActivityLogEntry = {
        id: `${Date.now().toString()}_activity`,
        timestamp: new Date(),
        ...action.payload,
      };
      return { ...state, activityLog: [...state.activityLog, activityEntry] };
    },
  },
});

export const { addNote, toggleNotePin, addActivityLogEntry, setNotes, setLoading, setError, clearError } = notesSlice.actions;
export default notesSlice.reducer; 